/**
 * Bakes shape text's *visual* scanline rows into the wire's `TextLine`s.
 *
 * yb360 stores pre-flowed text: each `<TL>` is one visual line carrying a left margin `LM`, and
 * each `<TR>` carries `TA` (x advance, absolute — it already includes `LM`) and `BL` (baseline,
 * absolute from the top of the frame). On load it paints those baked lines verbatim — `odxml.js`
 * sets `needRender = false` (1330) and `ODSprite.drawText` skips composition on the first draw
 * (odeditorsvg.js:6185), so tspans go straight to `(sprite.padding + TA, sprite.padding + BL)`
 * (odxml.js:1444).
 *
 * larry's in-memory model is *logical*: one `TextLine` per paragraph, wrapping recomputed on every
 * render by the scanline engine. Handing that model straight to the wire wrote a single `<TL>` with
 * `LM="0"`, one `<TR>` at `TA="0"`, and a text width wider than the shape — so yb360 drew the whole
 * paragraph as one long line from the bounding box's left edge, spilling outside the outline
 * instead of flowing inside it.
 *
 * This module closes that gap by re-using the very engine that renders the text on canvas
 * (`getShapeTextFlow` + `layoutShapeTextContent`, exactly as `fitParagraphsToShape` does for
 * capacity), so the XML is a faithful transcript of what larry displays.
 *
 * Verified against yb360's own wrapped circle (`docs/investigation.md`): a 120x120 circle whose
 * three lines carry `LM="42/19/9"`, `TV="36/82/102"`, `BL="13/27.4/41.8"`. For each line,
 * `LM === chordStart + (chordWidth - TV) / 2` holds *exactly* when the chord is measured on the
 * full shape — which pins `LM` and `BL` to the shape's own top-left corner with no padding of their
 * own (`ODSprite.getPadding()` is just the stroke thickness, odeditorsvg.js:5261-5287, and that
 * circle has no `<LS>` at all). larry's `chunk.x` / `row.baselineY` are already in that same frame
 * and unit, so neither needs converting.
 *
 * The baked form is **write-only**. larry re-flows from `paragraphsToPlainText` on every render and
 * never reads `LM`/`BL`/`TV` back, so this is a transcript for yb360's benefit, not a second source
 * of truth. The read path un-bakes it (`unbakeShapeTextParagraphs` in `shape-text.ts`) to keep the
 * in-memory model logical — one `TextLine` per paragraph — no matter which side it arrived from.
 */

import type { Paragraph, TextLine, TextRun } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type ShapeTextChunk,
  type ShapeTextRow,
  applyPerLineLeadingToFlow,
  buildCharRunMap,
  layoutShapeTextWithPerLineLeading,
  paragraphsToPlainText,
} from '@/lib/shapes/shape-text'
import { getShapeTextFlowBoundary } from '@/lib/boundaries/text-boundary'
import { layoutShapeTextWithDropCap } from '@/lib/shapes/shape-drop-cap'
import { buildShapeTextMeasurer, getSharedMeasureContext } from '@/lib/shapes/shape-text-measure'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import { shapeTextFlowWithWrap } from '@/lib/shapes/shape-text-wrap-bands'
import { coalesceWireRuns, ensureTextRunDefaults } from '@/lib/elements/wire-paragraphs'
import { fillTextLineWireMetrics } from '@/lib/text/fill-text-run-wire-metrics'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

/** One visual row: the chunks that share a `yTop`, plus the flow row they belong to. */
interface BakedRow {
  row: ShapeTextRow
  chunks: ShapeTextChunk[]
  /** Flat-text index of the row's first character. */
  startCharIndex: number
}

/**
 * Groups chunks into visual rows. Chunks arrive in flow order and `yTop` only ever increases (a
 * paragraph break jumps to a later row, never back), so consecutive runs of equal `yTop` are the
 * rows — no keyed lookup, and row order is preserved by construction.
 *
 * A single row legitimately holds several chunks: one per scanline segment when the outline is
 * concave (a star's arm, a heart's cleft), and one per justification gap when the row was
 * justified (`shape-text.ts` `applyRowAlignment`).
 */
function groupChunksIntoRows(chunks: ShapeTextChunk[], rows: ShapeTextRow[]): BakedRow[] {
  const baked: BakedRow[] = []

  for (const chunk of chunks) {
    const last = baked[baked.length - 1]

    if (last && last.row.yTop === chunk.yTop) {
      last.chunks.push(chunk)
      continue
    }

    const row = rows.find((r) => r.yTop === chunk.yTop)

    // Defensive: a chunk always comes from a row of this very flow, so this cannot normally miss.
    if (!row) continue
    baked.push({ row, chunks: [chunk], startCharIndex: chunk.startCharIndex })
  }

  return baked
}

/**
 * Slices `[startIndex, endIndex)` of the flat text into wire runs, carrying each character's source
 * run style across the row boundary.
 *
 * Deliberately driven by character index rather than by concatenating `chunk.text`: the flat text is
 * partitioned, so no character can be lost at a wrap point or a segment jump. `charRunMap` holds
 * `null` at the paragraph separators (the `\n`s `paragraphsToPlainText` joins with) — those are
 * structural, carry no glyph, and are skipped here.
 */
function sliceRunsForRange(
  text: string,
  charRunMap: Array<TextRun | null>,
  startIndex: number,
  endIndex: number
): TextRun[] {
  const runs: TextRun[] = []
  let currentSource: TextRun | null = null

  for (let i = startIndex; i < endIndex; i++) {
    const source = charRunMap[i] ?? null

    // Paragraph separator — no glyph, and it must not join two paragraphs' runs together.
    if (source === null) {
      currentSource = null
      continue
    }

    if (source === currentSource && runs.length > 0) {
      runs[runs.length - 1].textContent = (runs[runs.length - 1].textContent ?? '') + text[i]
      continue
    }

    currentSource = source
    runs.push({ ...source, textContent: text[i] })
  }

  // Adjacent source runs that happen to share a style still collapse into one `<TR>` — yb360 stores
  // shape text one run per word/style span, which is what the existing shape path already did via
  // `buildWireParagraphs(..., coalesceRuns = true)`.
  return coalesceWireRuns(runs.map(ensureTextRunDefaults))
}

/** Number of paragraph separators before `index` — i.e. which paragraph that offset falls in. */
function paragraphIndexAt(charRunMap: Array<TextRun | null>, index: number): number {
  let separators = 0

  for (let i = 0; i < index && i < charRunMap.length; i++) {
    if (charRunMap[i] === null) separators++
  }

  return separators
}

/**
 * The row's line-box width, yb360's `TV`.
 *
 * `TV` is *not* the measured text width — `ODTextLine` keeps `this.width` and `this.textWidth` as
 * separate fields (odeditorsvg.js:12202,12211) and only `this.width` is serialised
 * (`setAttribute('TV', this.width)`, 13218), defaulting to the frame's own width. Both fixtures
 * agree: yb360's circle line 2 has `TV="82"` against runs summing to 77.38, and
 * `Text_Example_NSD.xml` has a `TV="166"` line whose single run is `TW="4.43"`. The closest true
 * analogue larry has is the row's height-aware segment width, which is what its own layout
 * allocated the line.
 */
function rowBoxWidth(row: ShapeTextRow, leftMargin: number): number {
  const containing = row.segments.find(
    (s) => leftMargin >= s.startX - 0.01 && leftMargin <= s.endX + 0.01
  )
  const segment =
    containing ??
    row.segments.reduce<ShapeTextRow['segments'][number] | null>(
      (best, s) => (best == null || s.endX - s.startX > best.endX - best.startX ? s : best),
      null
    )

  return segment ? segment.endX - segment.startX : 0
}

/**
 * Converts logical paragraphs into wire paragraphs whose `textLines` are the shape's visual rows.
 *
 * Returns `null` when no layout is possible — no text, or a shape too small to hold a single row —
 * so the caller can fall back to the logical form rather than emit an empty frame.
 *
 * Text past the last fitting row is **not dropped**. Shape text has no overflow model of its own
 * (`fitParagraphsToShape`'s header: "anything past the last fitting row is simply not laid out"),
 * and `layoutShapeTextContent` stops at `consumedLength`. Emitting only the laid-out rows would
 * delete that tail from `content.text` — which `readShapeTextParagraphsFromDecoded` prefers over
 * `editorShape.paragraphs`, so it would be gone on reload too. Instead the remainder is
 * appended as `isHidden` lines, which is yb360's own overset mechanism: read at odxml.js:1364,
 * skipped for painting at 1442, and surfaced as `sprite.content.overflowText = true` at 1447.
 */
export function bakeShapeTextWireLines(
  paragraphs: Paragraph[],
  shape: ShapeData,
  fonts?: ParagraphBlockFontLookup,
  /** Text-wrap context. Omit and the baked lines describe an unwrapped layout — which would not be
   *  what the canvas displays for a wrapped frame, so pass it whenever it is available. */
  wrap?: ShapeTextWrapContext,
  /**
   * Whether this frame has a next frame in its text chain — the renderer's own
   * `breakTrailingWordAtCapacity` (`use-shape-text-layout.ts`).
   *
   * `fitParagraphsToShape`'s header used to assert this function "needs no flag of its own", on the
   * grounds that re-laying kept text out under the default word policy is a fixed point. That holds
   * for a frame whose content already fits, which is the settled state of every chain member — but
   * not while one is carrying overset, and the whole point of this function is that the overset tail
   * is what it partitions into `isHidden` lines. Measuring that partition under a different word
   * policy than the canvas draws puts the boundary in a different place, so the wire disagrees with
   * the screen about which characters are visible. Pass it whenever the chain state is known.
   */
  hasNextInChain?: boolean
): Paragraph[] | null {
  if (paragraphs.length === 0) return null

  const text = paragraphsToPlainText(paragraphs)

  if (text.length === 0) return null

  // Same measurement path the canvas renders with, so the XML transcribes what larry displays
  // (the reason `fitParagraphsToShape` shares it too — WMPCM-7988).
  const measurer = buildShapeTextMeasurer(paragraphs, fonts)
  const flow = shapeTextFlowWithWrap(
    shape,
    getShapeTextFlowBoundary(shape, measurer.pointPt, measurer.leadingMult),
    wrap
  )

  if (flow.rows.length === 0) return null

  const alignments = paragraphs.map((paragraph) => paragraph.alignment ?? 0)
  const charRunMap = buildCharRunMap(paragraphs)
  // The wire's per-line `BL` (baseline) must reflect each line's own resolved leading, not the
  // frame-wide probe pitch — `leadedFlow.rows`/`chunks` carry the remapped Y positions. Both come
  // out of the same settle call (drop cap, then per-line leading), which is what keeps
  // `groupChunksIntoRows`' exact-`yTop` match valid: rows and chunks are always from the pass that
  // won — reclaim included.
  const { flow: leadedFlow, layout } = layoutShapeTextWithPerLineLeading(
    shape,
    flow,
    text,
    measurer.measureTextPt,
    charRunMap,
    alignments,
    { breakTrailingWordAtCapacity: hasNextInChain },
    (f) => shapeTextFlowWithWrap(shape, f, wrap),
    (candidate) => {
      const dropCapResult = layoutShapeTextWithDropCap(
        text,
        candidate,
        measurer.measureTextPt,
        paragraphs,
        measurer.charRunMap,
        getSharedMeasureContext(),
        fonts,
        alignments,
        { breakTrailingWordAtCapacity: hasNextInChain }
      )

      return applyPerLineLeadingToFlow(
        dropCapResult.flow,
        dropCapResult.layout,
        charRunMap,
        measurer.rowMetricPt
      )
    }
  )
  const { chunks, consumedLength } = layout
  const bakedRows = groupChunksIntoRows(chunks, leadedFlow.rows)

  if (bakedRows.length === 0) return null
  const linesByParagraph = new Map<number, TextLine[]>()
  const pushLine = (paragraphIndex: number, line: TextLine) => {
    const existing = linesByParagraph.get(paragraphIndex)

    if (existing) existing.push(line)
    else linesByParagraph.set(paragraphIndex, [line])
  }

  bakedRows.forEach((baked, i) => {
    // Contiguous partition of the flat text: this row runs up to the next row's first character,
    // and the last laid-out row up to the overflow boundary.
    const endIndex = bakedRows[i + 1]?.startCharIndex ?? consumedLength

    // One sub-range per chunk, not one range for the whole row: an obstacle can split a row into
    // chunks that resume at very different x positions (text wraps around the shape and picks
    // back up on its far side). Slicing separately per chunk boundary — instead of the row's full
    // `[startCharIndex, endIndex)` in one call — keeps each chunk's own `x` alongside its runs, so
    // the gap between chunks survives into the wire metrics below instead of being bridged.
    const segments = baked.chunks
      .map((chunk, chunkIndex) => ({
        x: chunk.x,
        runs: sliceRunsForRange(
          text,
          charRunMap,
          chunk.startCharIndex,
          baked.chunks[chunkIndex + 1]?.startCharIndex ?? endIndex
        ),
      }))
      .filter((segment) => segment.runs.length > 0)

    if (segments.length === 0) return

    const runs = segments.flatMap((segment) => segment.runs)
    // One break per chunk after the first: the run index its runs start at, and the x the cursor
    // must jump to there instead of continuing from the previous chunk's end.
    const segmentBreaks: Array<{ runIndex: number; x: number }> = []
    let runIndex = 0

    segments.forEach((segment, segmentIndex) => {
      if (segmentIndex > 0) segmentBreaks.push({ runIndex, x: segment.x })
      runIndex += segment.runs.length
    })

    const leftMargin = segments[0].x
    const line = fillTextLineWireMetrics(
      { leftMargin, rightMargin: 0, isHidden: false, textRuns: runs },
      fonts,
      baked.row.baselineY,
      segmentBreaks
    )

    pushLine(paragraphIndexAt(charRunMap, baked.startCharIndex), {
      ...line,
      // yb360 records the line's allocated box width here, not the measured text width.
      textWidth: rowBoxWidth(baked.row, leftMargin),
    })
  })

  // Overset tail — kept on the wire, hidden from paint (see the doc comment above). Split per
  // paragraph so each `<PG>` keeps its own remainder.
  if (consumedLength < text.length) {
    let start = consumedLength

    while (start < text.length) {
      const paragraphIndex = paragraphIndexAt(charRunMap, start)
      let end = start

      while (end < text.length && charRunMap[end] !== null) end++
      const runs = sliceRunsForRange(text, charRunMap, start, end)

      if (runs.length > 0) {
        pushLine(
          paragraphIndex,
          fillTextLineWireMetrics(
            { leftMargin: 0, rightMargin: 0, isHidden: true, textRuns: runs },
            fonts
          )
        )
      }

      // Skip the separator itself; `paragraphIndexAt` counts it for the next slice.
      start = end + 1
    }
  }

  return paragraphs.map((paragraph, index) => {
    const baked = linesByParagraph.get(index)

    // A paragraph that produced no rows is an intentionally empty one (a blank line between two
    // paragraphs of text). Keep its original styled line so the blank — and the style a user would
    // type into it — survives the round trip; dropping it would silently delete the paragraph.
    if (!baked || baked.length === 0) {
      const fallback = (paragraph.textLines ?? []).map((line) =>
        fillTextLineWireMetrics(
          { ...line, textRuns: (line.textRuns ?? []).map(ensureTextRunDefaults) },
          fonts
        )
      )

      return { ...paragraph, textLines: fallback.length > 0 ? fallback : undefined }
    }

    return { ...paragraph, textLines: baked }
  })
}
